import {
  CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Input as MantineInput } from '@mantine/core';
import { useId, useUncontrolled } from '@mantine/hooks';
import { KeyCode } from 'ag-grid-community';
import clsx from 'clsx';

import { ClearButton } from '../clearbutton/ClearButton';
import { ErrorMessage } from '../error-message/ErrorMessage';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';
import { Tooltip } from '../tooltip/Tooltip';

import styles from './input.module.scss';

export type InputProps = {
  classNames?: {
    root?: string;
    input?: string;
  };
  'data-testid'?: string;
  disabled?: boolean;
  defaultValue?: string;
  description?: ReactNode;
  clearable?: boolean;
  error?: string | boolean;
  inlineError?: boolean;
  label?: ReactNode | ReactNode[];
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  loading?: boolean;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onCommit?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  /** When true, shows a tooltip with the current input value when the text is truncated */
  withTooltip?: boolean;
  value?: string;
  style?: CSSProperties;
  tableLayout?: boolean;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  tabIndex?: number;
};

export function Input({
  classNames,
  clearable,
  'data-testid': dataTestId = 'text-input',
  defaultValue,
  description,
  disabled,
  readOnly,
  error,
  inlineError,
  label,
  required,
  leftSection,
  loading,
  onChange,
  onCommit,
  placeholder,
  withTooltip,
  value,
  style,
  tableLayout,
  type,
  autoComplete,
  tabIndex,
  ...props
}: InputProps) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [valueOnFocus, setValueOnFocus] = useState<string | undefined>(
    undefined
  );
  const [isTooltipTargetHovered, setIsTooltipTargetHovered] = useState(false);
  const [tooltipDisabled, setTooltipDisabled] = useState(true);

  // we use this to track changes and toggle the clear action
  const [text, setText] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });

  // Re-evaluate overflow while hovered so tooltip visibility tracks text changes.
  useEffect(() => {
    if (!withTooltip) {
      return;
    }

    if (!isTooltipTargetHovered) {
      setTooltipDisabled(true);
      return;
    }

    const input = ref.current;
    if (!input) {
      return;
    }

    const isOverflowing = input.scrollWidth > input.clientWidth;
    setTooltipDisabled(!isOverflowing);
  }, [isTooltipTargetHovered, text, withTooltip]);

  const handleTooltipMouseEnter = withTooltip
    ? () => {
        setIsTooltipTargetHovered(true);
      }
    : undefined;

  const handleTooltipMouseLeave = withTooltip
    ? () => {
        setIsTooltipTargetHovered(false);
        setTooltipDisabled(true);
      }
    : undefined;

  function commit(value: string) {
    if (valueOnFocus !== value) {
      onCommit?.(value);
      setValueOnFocus(value);
    }
  }

  let rightSection: ReactNode | undefined;
  if (loading) {
    rightSection = <LoadingIndicator data-testid="input-loading-indicator" />;
  } else if (clearable && !disabled && text != null && text.length > 0) {
    rightSection = (
      <ClearButton
        onClear={() => {
          if (ref.current && ref.current.value !== '') {
            ref.current.value = '';
            setText('');
            commit('');
          }
        }}
      />
    );
  } else if (props.rightSection) {
    rightSection = props.rightSection;
  }

  const content = (
    <div
      className={clsx(classNames?.root, {
        [styles['root--table-layout']]: tableLayout,
      })}
    >
      <FormLabel
        description={description}
        id={id}
        required={required}
        disabled={disabled}
        loading={loading}
        error={error}
        inline={inlineError}
        label={label}
      />
      <MantineInput
        style={style}
        id={id}
        data-testid={dataTestId}
        type={type}
        autoComplete={autoComplete}
        tabIndex={tabIndex}
        disabled={disabled || loading}
        error={error}
        leftSection={leftSection}
        readOnly={readOnly}
        onFocus={() => {
          setHasFocus(true);
          setValueOnFocus((ref.current && ref.current.value) ?? undefined);
        }}
        onBlur={() => {
          commit(text);
          setHasFocus(false);
        }}
        onChange={({ target }) => setText(target.value)}
        onKeyUp={({ code }) => {
          if (code === KeyCode.ENTER) {
            // blur and thus trigger commit hook
            ref.current?.blur();
          }
        }}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        placeholder={placeholder}
        ref={ref}
        rightSection={rightSection}
        // title={label} @todo
        unstyled
        value={text}
        classNames={{
          wrapper: clsx(
            styles['wrapper'],
            hasFocus && styles['wrapper--is-focussed']
          ),
          input: clsx(styles['input'], classNames?.input),
          section: styles['section'],
        }}
      />
      {typeof error === 'string' && error !== '' && !inlineError && (
        <ErrorMessage className={styles['input-error']} role="alert">
          {error}
        </ErrorMessage>
      )}
    </div>
  );

  if (withTooltip) {
    return (
      <Tooltip
        disabled={tooltipDisabled}
        label={text ?? ''}
        openDelay={500}
        position="bottom-start"
      >
        {content}
      </Tooltip>
    );
  }

  return content;
}
